import { Route, Routes, Link, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useRef, useCallback } from 'react';
import Register from "../../containers/Register/Register";
import Login from "../../containers/Login/Login";
import Forwarding from "../../containers/Forwarding/Forwarding";
import Home from "../../containers/Home/Home";
import Privacy from "../../containers/Privacy/Privacy";
import Terms from "../../containers/Terms/Terms";
import ChangePassword from "../../containers/ChangePassword/ChangePassword";
import RegisterDomainContainer from "../../containers/CustomDomain/RegisterDomainContainer";
import ForgotPassword from "../../containers/ForgotPassword/ForgotPassword";
import VerifyAccount from "../../containers/VerifyAccount/VerifyAccount";
import ManageSubscriptionContainer from "../../containers/ManageSubscription/ManageSubscriptionContainer";
import Settings from "../../containers/Settings/Settings";
import Footer from "../../containers/Footer/Footer";
import PropTypes from 'prop-types';
import { Alert } from 'reactstrap';
import { NavLink as RRNavLink, useNavigate } from 'react-router-dom';
import { userActions } from '../../actions';
import { virtualEmailActions } from '../../actions';
import logo from '../../logo.svg';
import { Navbar, NavbarBrand, Collapse, Nav, NavItem, NavLink, Container } from 'reactstrap';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faEnvelope, faKey } from '@fortawesome/free-solid-svg-icons';
import SubscriptionContainer from '../../containers/Subscribe/SubscriptionContainer';
import SubscriptionOptionsContainer from '../../containers/Subscribe/SubscriptionOptionsContainer';
import PaymentSettingsContainer from '../../containers/PaymentSettings/PaymentSettingsContainer';
import { selectAlert } from '../../redux/modules/alertReducer';
import { selectVerified } from '../../redux/modules/memberReducer';
import { selectSubscription } from '../../redux/modules/subscriptionReducer';
import { selectLoggedIn, selectAuthChecked } from '../../redux/modules/authReducer';
import { paths } from '../../constants';
import './App.css';

library.add(faEnvelope, faKey);

export const PageNotFound = (props, context = {}) => {
  if (context.setStatus) {
    context.setStatus(404);
  }

  return (
    <div className="not-found-page">
      <h1>Page not found</h1>
      <Link to="/">Go back home</Link>
    </div>
  );
};

PageNotFound.contextTypes = {
  setStatus: PropTypes.func.isRequired
};

const App = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(selectLoggedIn);
  const authChecked = useSelector(selectAuthChecked);
  const alert = useSelector(selectAlert);
  const isVerified = useSelector(selectVerified);
  const subscription = useSelector(selectSubscription);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Check auth status on app load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.forEach((value, key) => {
      if (key.startsWith("utm")) {
        localStorage.setItem(key, value);
      }
    });
    // Always check auth on mount
    dispatch(userActions.refreshAuth());
  }, [dispatch]);

  // Fetch user data after auth is confirmed
  useEffect(() => {
    if (authChecked && isLoggedIn) {
      dispatch(virtualEmailActions.getSummary(false, navigateRef.current));
    }
  }, [dispatch, authChecked, isLoggedIn]);

  const handleLogout = useCallback(() => {
    dispatch(userActions.logout());
  }, [dispatch]);

  let alertColor = "primary";
  if (alert && alert.type === "alert-danger") {
    alertColor = "danger";
  }

  const themedLogo = logo;
  let logoLink = isLoggedIn ? paths.forwarding : '/';

  return (
    <div className="defaultFont app-layout">
      {alert && alert.message && (
        <Alert color={alertColor} className="app-alert">
          {alert.message} {alert.link && <Link to={alert.link}>{alert.linkText}</Link>}
        </Alert>
      )}

      <header>
        <Navbar light expand="md" className="app-navbar">
          <Container>
            {isLoggedIn && !isVerified && (
              <NavbarBrand>
                <img src={themedLogo} alt="ID Unseen" />
              </NavbarBrand>
            )}
            {(!isLoggedIn || isVerified) && (
              <NavbarBrand tag={RRNavLink} to={logoLink}>
                <img src={themedLogo} alt="ID Unseen" />
              </NavbarBrand>
            )}

            <Collapse isOpen navbar>
              {isLoggedIn && isVerified && (
                <>
                  <Nav className="me-auto" navbar>
                    <NavItem>
                      <NavLink tag={RRNavLink} to={paths.forwarding}>
                        Forwarding
                      </NavLink>
                    </NavItem>
                    {!subscription.isCustomDomainSubscription && (
                      <NavItem>
                        <NavLink tag={RRNavLink} to={paths.subscriptionLandingPage}>
                          Subscribe
                        </NavLink>
                      </NavItem>
                    )}
                    <NavItem>
                      <NavLink tag={RRNavLink} to={paths.account}>
                        Account
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <Nav navbar>
                    <NavItem>
                      <NavLink tag={RRNavLink} to={paths.login} onClick={handleLogout} className="nav-logout">
                        Logout
                      </NavLink>
                    </NavItem>
                  </Nav>
                </>
              )}

              {isLoggedIn && !isVerified && (
                <Nav className="ms-auto" navbar>
                  <NavItem>
                    <NavLink tag={RRNavLink} to={paths.login} onClick={handleLogout} className="nav-logout">
                      Logout
                    </NavLink>
                  </NavItem>
                </Nav>
              )}

              {!isLoggedIn && (
                <Nav className="ms-auto" navbar>
                  <NavItem>
                    <NavLink tag={RRNavLink} to={paths.login}>
                      Login
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink tag={RRNavLink} to={paths.register}>
                      Register
                    </NavLink>
                  </NavItem>
                </Nav>
              )}
            </Collapse>
          </Container>
        </Navbar>
      </header>

      <main>
        <Routes>
          <Route path={paths.verifyEmail} element={<PrivateRoute component={VerifyAccount} />} />
          <Route path={paths.mailSettings} element={<PrivateRoute component={Settings} />} />
          <Route path={paths.subscriptionSettings} element={<PrivateRoute component={ManageSubscriptionContainer} />} />
          <Route path={paths.customDomainSubscribe} element={<PrivateRoute component={RegisterDomainContainer} />} />
          <Route path={paths.serviceSubscribe} element={<PrivateRoute component={SubscriptionContainer} />} />
          <Route path={paths.subscriptionLandingPage} element={<PrivateRoute component={SubscriptionOptionsContainer} />} />
          <Route path={paths.paymentSettings} element={<PrivateRoute component={PaymentSettingsContainer} />} />
          <Route path={paths.changePassword} element={<PrivateRoute component={ChangePassword} />} />
          <Route path={paths.account} element={<PrivateRoute component={Settings} />} />
          <Route path={paths.forwarding} element={<PrivateRoute component={Forwarding} />} />
          <Route path={paths.login} element={<Login />} />
          <Route path={paths.register} element={<Register />} />
          <Route path="/forgotPassword/" element={<ForgotPassword />} />
          <Route path="/terms-of-service/" element={<Terms />} />
          <Route path="/privacy-policy/" element={<Privacy />} />
          <Route exact path="/" element={<Home />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
};

function PrivateRoute({ component: Component, ...rest }) {
  const authed = useSelector(selectLoggedIn);
  const authChecked = useSelector(selectAuthChecked);
  const location = useLocation();

  // Wait for auth check before deciding
  if (!authChecked) {
    return null;
  }

  return authed ? (
    <Component {...rest} />
  ) : (
    <Navigate to="/login" state={{ from: location }} replace />
  );
}

export { App };
